import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuthApi } from "../hooks/api/axiosInterceptor";

const CalendarWrapper = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
`;

const NoticeSection = styled.div`
  background-color: #e8f0fe;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 2rem;
  text-align: center;
`;

const NoticeText = styled.p`
  color: #1a4786;
  font-weight: 500;
  margin: 0;
`;

const CalendarSection = styled.div`
  width: 100%;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const MonthYearDisplay = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: 1rem;
`;

const NavButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: #2056ad;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: #1a4786;
  }
`;

const WeekDays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 0.5rem;
`;

const WeekDayLabel = styled.div`
  text-align: center;
  font-weight: bold;
  padding: 0.5rem;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border: 1px solid #ddd;
`;

const DayCell = styled.div`
  min-height: 100px; /* 더 크게 조정 */
  border: 1px solid #ddd;
  padding: 0.5rem;
  background-color: ${props =>
    props.isToday ? '#e8f0fe' :
        props.isSelected ? '#d3e4fd' :
            props.isOtherMonth ? '#f5f5f5' : 'white'
};
  cursor: pointer;
  position: relative;
  
  &:hover {
    background-color: ${props => props.isSelected ? '#d3e4fd' : '#f8f9fa'};
  }

  @media (min-width: 1200px) {
    min-height: 120px; /* 큰 화면에서 더 크게 */
  }
`;

const DayNumber = styled.div`
  font-weight: ${props => props.isToday ? 'bold' : 'normal'};
  color: ${props =>
    props.isOtherMonth ? '#999' :
        props.isToday ? '#2056ad' : 'black'
};
  margin-bottom: 0.25rem;
`;

const EventText = styled.div`
  font-size: 0.75rem;
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  background-color: ${props => props.color || '#e8f0fe'};
  padding: 2px 4px;
  border-radius: 2px;
  margin-bottom: 3px;
`;

const EventListSection = styled.div`
  width: 100%;
  margin-top: 2rem;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const EventListHeader = styled.div`
  background-color: #2056ad;
  color: white;
  padding: 1rem;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  font-size: 1.25rem;
  font-weight: bold;
`;

const EventListContainer = styled.div`
  background-color: #f8f9fa;
  padding: 1rem;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  max-height: 300px;
  overflow-y: auto;
`;

const EventList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
`;

const EventItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
`;

const EventContent = styled.span`
  flex: 1;
`;

const EmptyMessage = styled.div`
  text-align: center;
  color: #6c757d;
  padding: 2rem 0;
`;

// 랜덤 색상 생성기
const getEventColor = (event) => {
    let hash = 0;
    for (let i = 0; i < event.length; i++) {
        hash = event.charCodeAt(i) + ((hash << 5) - hash);
    }
    const h = Math.abs(hash) % 360;
    return `hsl(${h}, 70%, 85%)`;
};
const CalendarReadPage = () => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [events, setEvents] = useState({});
    const [eventColors, setEventColors] = useState({});

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await useAuthApi.get('/events'); // 실제 API 경로로 수정
                const serverEvents = response.data.data;

                const eventMap = {};
                serverEvents.forEach(item => {
                    const date = item.event_date;
                    const contents = item.content.split(',').map(s => s.trim());
                    if (!eventMap[date]) {
                        eventMap[date] = [];
                    }
                    eventMap[date].push(...contents);
                });

                setEvents(eventMap);
            } catch (error) {
                console.error('이벤트 불러오기 실패:', error);
            }
        };

        fetchEvents();
    }, []);

    useEffect(() => {
        const colors = {};
        Object.keys(events).forEach(date => {
            const eventList = Array.isArray(events[date]) ? events[date] : [events[date]];
            eventList.forEach(event => {
                if (!colors[event]) {
                    colors[event] = getEventColor(event);
                }
            });
        });
        setEventColors(colors);
    }, [events]);

    const formatDate = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const handlePrevMonth = () => {
        setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1));
    };

    const handleNextMonth = () => {
        setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1));
    };

    const handleDayClick = (day, month, year) => {
        const newSelectedDate = new Date(year, month, day);
        setSelectedDate(newSelectedDate);
    };

    const getDaysInMonth = (year, month) => {
        return new Date(year, month + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (year, month) => {
        return new Date(year, month, 1).getDay();
    };

    const renderCalendar = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const daysInMonth = getDaysInMonth(year, month);
        const firstDay = getFirstDayOfMonth(year, month);
        const today = new Date();

        const days = [];
        const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

        const prevMonthDays = getDaysInMonth(year, month - 1);
        for (let i = firstDay - 1; i >= 0; i--) {
            const day = prevMonthDays - i;
            const date = new Date(year, month - 1, day);
            const dateKey = formatDate(date);
            days.push(
                <DayCell key={`prev-${day}`} isOtherMonth onClick={() => {
                    setCurrentDate(new Date(year, month - 1));
                    handleDayClick(day, month - 1, year);
                }}>
                    <DayNumber isOtherMonth>{day}</DayNumber>
                    {events[dateKey]?.map((event, idx) => (
                        <EventText key={idx} color={eventColors[event]}>
                            {event.slice(0, 10)}...
                        </EventText>
                    ))}
                </DayCell>
            );
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dateKey = formatDate(date);
            const isToday = today.toDateString() === date.toDateString();
            const isSelected = selectedDate.toDateString() === date.toDateString();
            days.push(
                <DayCell key={day} isToday={isToday} isSelected={isSelected} onClick={() => handleDayClick(day, month, year)}>
                    <DayNumber isToday={isToday}>{day}</DayNumber>
                    {events[dateKey]?.map((event, idx) => (
                        <EventText key={idx} color={eventColors[event]}>
                            {event.length > 10 ? `${event.slice(0, 10)}...` : event}
                        </EventText>
                    ))}
                </DayCell>
            );
        }

        const remainingCells = 42 - days.length;
        for (let day = 1; day <= remainingCells; day++) {
            const date = new Date(year, month + 1, day);
            const dateKey = formatDate(date);
            days.push(
                <DayCell key={`next-${day}`} isOtherMonth onClick={() => {
                    setCurrentDate(new Date(year, month + 1));
                    handleDayClick(day, month + 1, year);
                }}>
                    <DayNumber isOtherMonth>{day}</DayNumber>
                    {events[dateKey]?.map((event, idx) => (
                        <EventText key={idx} color={eventColors[event]}>
                            {event.length > 10 ? `${event.slice(0, 10)}...` : event}
                        </EventText>
                    ))}
                </DayCell>
            );
        }

        return (
            <>
                <WeekDays>
                    {weekDays.map(day => (
                        <WeekDayLabel key={day}>{day}</WeekDayLabel>
                    ))}
                </WeekDays>
                <CalendarGrid>{days}</CalendarGrid>
            </>
        );
    };

    const selectedDateEvents = events[formatDate(selectedDate)] || [];

    return (
        <CalendarWrapper>
            <CalendarSection>
                <CalendarHeader>
                    <MonthYearDisplay>
                        {currentDate.getFullYear()}년 {currentDate.getMonth() + 1}월
                    </MonthYearDisplay>
                    <NavigationButtons>
                        <NavButton onClick={handlePrevMonth}>이전</NavButton>
                        <NavButton onClick={handleNextMonth}>다음</NavButton>
                    </NavigationButtons>
                </CalendarHeader>
                {renderCalendar()}
            </CalendarSection>

            <EventListSection>
                <EventListHeader>
                    {selectedDate.getFullYear()}년 {selectedDate.getMonth() + 1}월 {selectedDate.getDate()}일 일정
                </EventListHeader>
                <EventListContainer>
                    {selectedDateEvents.length > 0 ? (
                        <EventList>
                            {selectedDateEvents.map((event, idx) => (
                                <EventItem key={idx} style={{ borderLeft: `4px solid ${eventColors[event]}` }}>
                                    <EventContent>{event}</EventContent>
                                </EventItem>
                            ))}
                        </EventList>
                    ) : (
                        <EmptyMessage>일정이 없습니다.</EmptyMessage>
                    )}
                </EventListContainer>
            </EventListSection>
        </CalendarWrapper>
    );
};

export default CalendarReadPage;